import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Building,
  MapPin,
  Star,
  ArrowRight,
  Users,
  ShoppingBag
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const WestMelbournePage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Regular cleaning for West Melbourne families and homeowners. Weekly or bi-weekly service with the same trusted team every visit."
    },
    {
      title: "Deep Cleaning",
      description: "Comprehensive cleaning for established and growing West Melbourne homes. Perfect for seasonal refreshes or getting back on track."
    },
    {
      title: "Move-In / Move-Out Cleaning",
      description: "Inspection-ready cleaning for home transitions. Trusted by renters, homeowners, landlords, and property managers throughout West Melbourne."
    },
    {
      title: "Post-Construction Cleaning",
      description: "Final cleanup after renovations or new construction, removing dust and debris so you can move in or show your finished space."
    }
  ];

  const perfectFor = [
    "Families in established West Melbourne neighborhoods",
    "Homeowners in new developments and growing areas",
    "Busy professionals balancing work and home life",
    "Landlords managing rental properties",
    "Properties near I-95 and Minton Road corridors",
    "Residents in the West Melbourne community"
  ];

  const faqs = [
    {
      question: "Do you serve all of West Melbourne?",
      answer: "Yes. We serve all West Melbourne neighborhoods—from established areas near US-192 to newer developments along Minton Road and throughout the community. West Melbourne is growing, and we're familiar with properties across the area."
    },
    {
      question: "Can you work around our family's busy schedule?",
      answer: "Absolutely. We understand families are juggling work, school, activities, and everything else. We're flexible and work with your availability to make cleaning convenient, not complicated."
    },
    {
      question: "What makes Pierce Premier different from other cleaning companies serving West Melbourne?",
      answer: "We're locally owned by Bob and Heather, not a franchise. You'll have the same cleaners every visit, and Heather's 15+ years of experience means we deliver consistent, professional results. We're accountable and easy to reach."
    },
    {
      question: "Do you handle post-construction cleaning?",
      answer: "Yes. Whether it's a renovation project or new construction, we handle the final cleanup—removing dust, debris, and construction residue so your space is clean and ready to use."
    },
    {
      question: "Can you accommodate urgent or last-minute requests?",
      answer: "We do our best. Call us at 321-408-1347 and let us know what you need. As a small, locally-owned business, we're often more flexible than larger companies."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in West Melbourne, FL | Pierce Premier",
      description: "Trusted house cleaning in West Melbourne, FL. Growing community specialists. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/west-melbourne"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "West Melbourne" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in West Melbourne",
      description: "Professional house cleaning services in West Melbourne, FL including recurring cleaning, deep cleaning, move-in/move-out, and post-construction cleanup."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in West Melbourne, FL | Pierce Premier"
        description="Trusted house cleaning in West Melbourne, FL. Growing community. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347."
        canonical="/areas/west-melbourne"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <Badge className="bg-green-600 text-white">Growing Community</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in West Melbourne, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  West Melbourne is a growing community where established neighborhoods meet new development—families building their lives, convenient access to I-95 and local shopping, and that blend of suburban comfort and accessibility. Pierce Premier serves this evolving area with cleaning services for homeowners who need reliability they can count on.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you're maintaining a family home, managing rental properties, or moving into a new place, we bring the same commitment to quality and professionalism to every job.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-green-600 text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-green-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80" 
                  alt="House cleaning services for families in West Melbourne, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why West Melbourne Residents Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Built for Growing Families</h3>
                  <p className="text-gray-600 leading-relaxed">
                    West Melbourne families are busy—work, kids, activities, and managing a household. We're reliable, flexible, and easy to work with, taking cleaning off your plate so you can focus on what matters.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team Every Time</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. You'll have the same cleaners every visit, so they learn your home, your preferences, and become familiar faces you can trust.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                    <ShoppingBag className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned & Accountable</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather personally oversee every job. We're not a franchise—we're a local business that stands behind our work and responds when you need us.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience to every West Melbourne home. We know what quality cleaning looks like, and we deliver it consistently.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving West Melbourne
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  West Melbourne has that dynamic quality—it's a community that's growing and evolving, with families moving in and building their lives here. There's a mix of established neighborhoods and new developments, and we appreciate serving residents across that spectrum.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you're in a home you've lived in for years or just settling into a new place, having reliable service providers makes life easier. We're glad to be part of keeping your West Melbourne home clean and comfortable.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Services for West Melbourne Homes
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-green-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Perfect For West Melbourne
              </h2>
              <p className="text-gray-600">Pierce Premier is trusted by:</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {perfectFor.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-green-100">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                West Melbourne Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Minton Road Corridor",
                "US-192 Area",
                "I-95 Corridor",
                "Eau Gallie Boulevard",
                "New Haven Avenue",
                "Residential Neighborhoods"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Codes:</span> 32904, 32919
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Palm Bay", link: "/areas/palm-bay" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Melbourne Beach", link: "/areas/melbourne-beach" },
                { name: "Indialantic", link: "/areas/indialantic" },
                { name: "Satellite Beach", link: "/areas/satellite-beach" }
              ].map((city, idx) => (
                <Link key={idx} to={city.link} className="p-4 bg-green-50 rounded-lg border-2 border-green-200 hover:border-green-400 transition-colors text-center group">
                  <span className="text-green-700 font-semibold group-hover:text-green-900">{city.name}</span>
                  <ArrowRight className="w-4 h-4 inline-block ml-1 text-green-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from West Melbourne Residents
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <CTASection 
          title="Ready to Simplify Your Life?"
          description="Let Pierce Premier handle the cleaning while you focus on your family and everything else that matters. Reliable service, consistent team, and quality you can count on."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default WestMelbournePage;
